import { useCallback, useEffect, useState } from 'react'
import { SlideOver } from '../components/SlideOver.js'
import { KpiTile } from '../components/KpiTile.js'
import { fetchPartners, fetchProductLines, saveProductLine } from '../lib/api.js'
import { formatGBP, formatPct } from '@shared/money'
import { priceProduct } from '@shared/pricing'
import type { LineSummary } from '@shared/productLines'
import type { Tier } from '@shared/types'

/**
 * Product Lines — Wave9 admin sets the channel discount and the channel
 * allow-list per HubSpot Product Line (decisions 42, 43).
 *
 * Two things this page exists to prevent:
 *  - a new Product Line appearing in HubSpot and silently being un-quotable
 *    (unconfigured lines are flagged and sort to the top), and
 *  - the ~quarter of the price book with no Product Line disappearing
 *    (they land in "(uncategorised)", which is a real, configurable row).
 *
 * ⚠️ Editing writes to `product_lines` and changes buy prices for every open
 * DRAFT. Sent quotes are frozen and unaffected.
 */

const EXAMPLE_LIST_PRICE = 1000

export function ProductLines() {
  const [rows, setRows] = useState<LineSummary[]>([])
  const [editable, setEditable] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<LineSummary | null>(null)
  const [tiers, setTiers] = useState<Tier[]>([])
  // Decision 178: the provisioning routing table + its pick lists.
  const [routing, setRouting] = useState<Record<string, { serviceType: string | null; assetPipelineId: string | null }>>({})
  const [serviceTypes, setServiceTypes] = useState<string[]>([])
  const [assetPipelines, setAssetPipelines] = useState<Array<{ id: string; label: string }>>([])

  const load = useCallback(() => {
    setLoading(true)
    fetchProductLines()
      .then((res) => {
        setRows(res.lines); setEditable(res.editable); setError(null)
        setRouting(res.routing ?? {})
        setServiceTypes(res.serviceTypeOptions ?? [])
        setAssetPipelines(res.assetPipelines ?? [])
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])
  // The tier ladder preview needs the real tiers, not a hard-coded set.
  useEffect(() => { fetchPartners().then((r) => setTiers(r.tiers)).catch(() => setTiers([])) }, [])

  if (loading) return <p className="muted">Loading Product Lines…</p>
  if (error) return <p className="pill pill--stop">{error}</p>

  const unconfigured = rows.filter((r) => r.unconfigured && r.productCount > 0)
  const visible = rows.filter((r) => r.channelVisible)
  const quotable = visible.reduce((s, r) => s + r.productCount, 0)
  const total = rows.reduce((s, r) => s + r.productCount, 0)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Product Lines</h1>
          <p>
            Channel discount and visibility default to a band per Product Line, so nothing needs
            maintaining across 157 products. A product can still override its band in HubSpot
            (<code>wave9_base_discount_pct</code> / <code>wave9_reseller_visible</code>) — the
            Overrides column counts those. Discounts here are scaled by each partner's tier.
          </p>
        </div>
      </div>

      {unconfigured.length > 0 && (
        <div className="banner">
          <strong>{unconfigured.length} unconfigured Product Line{unconfigured.length > 1 ? 's' : ''}.</strong>
          <span>
            {unconfigured.map((r) => `${r.name} (${r.productCount})`).join(', ')} — present in HubSpot with no
            band set, so partners can't quote them and they carry no discount.
          </span>
        </div>
      )}

      <div className="tiles">
        <KpiTile accent label="Quotable by partners" value={String(quotable)} sub={`of ${total} active products`} />
        <KpiTile label="Lines on the allow-list" value={`${visible.length} of ${rows.length}`} />
        <KpiTile
          label="Uncategorised products"
          value={String(rows.find((r) => r.name === '(uncategorised)')?.productCount ?? 0)}
          sub="no Product Line in HubSpot"
        />
      </div>

      <div className="card">
        <div className="card__head">
          <h2>Bands</h2>
          <span className="muted small">click a line to see its effect</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product Line</th>
                <th className="num">Products</th>
                <th className="num">Overrides</th>
                <th>Partners can quote</th>
                <th className="num">Discount off list</th>
                <th>Provisioning</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} onClick={() => setSelected(r)}>
                  <td>
                    <strong>{r.name}</strong>
                    {r.unconfigured && <span className="pill pill--warn" style={{ marginLeft: 8 }}>not configured</span>}
                  </td>
                  <td className="num">{r.productCount}</td>
                  <td className="num">
                    {r.overrideCount > 0
                      ? <span className="pill pill--info">{r.overrideCount}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    {r.channelVisible
                      ? <span className="pill pill--ok">yes</span>
                      : <span className="pill">no</span>}
                  </td>
                  <td className="num">{r.discountPct > 0 ? formatPct(r.discountPct) : <span className="muted">—</span>}</td>
                  <td className="small">
                    {routing[r.name]?.serviceType && routing[r.name]?.assetPipelineId
                      ? <span className="pill pill--ok">{routing[r.name]!.serviceType}</span>
                      : <span className="muted">unrouted</span>}
                  </td>
                  <td className="muted small">{r.notes ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <BandPanel
          line={selected}
          tiers={tiers}
          editable={editable}
          routing={routing[selected.name] ?? { serviceType: null, assetPipelineId: null }}
          serviceTypes={serviceTypes}
          assetPipelines={assetPipelines}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load() }}
        />
      )}
    </>
  )
}

/** What a band actually does to a partner's buy price, across the tier ladder. */
function BandPanel({
  line, tiers, editable, routing, serviceTypes, assetPipelines, onClose, onSaved,
}: {
  line: LineSummary
  tiers: Tier[]
  editable: boolean
  routing: { serviceType: string | null; assetPipelineId: string | null }
  serviceTypes: string[]
  assetPipelines: Array<{ id: string; label: string }>
  onClose: () => void
  onSaved: () => void
}) {
  const [discount, setDiscount] = useState(String(Math.round(line.discountPct * 1000) / 10))
  const [visible, setVisible] = useState(line.channelVisible)
  // Decision 178: the provisioning routing — '' = unrouted (rows flagged in the pack).
  const [serviceType, setServiceType] = useState(routing.serviceType ?? '')
  const [pipelineId, setPipelineId] = useState(routing.assetPipelineId ?? '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const pct = Number(discount)
  const valid = Number.isFinite(pct) && pct >= 0 && pct <= 100
  // An unconfigured line (new in HubSpot, no band yet) is always saveable —
  // saving CREATES the band, which also adds the line to the asset tag picker.
  const dirty = valid && (line.unconfigured || Math.abs(pct / 100 - line.discountPct) > 1e-9 || visible !== line.channelVisible
    || serviceType !== (routing.serviceType ?? '') || pipelineId !== (routing.assetPipelineId ?? ''))

  const save = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      // The API takes a FRACTION; the field is a percentage because that is how
      // people talk about discounts. Convert at the boundary, once.
      await saveProductLine({
        name: line.name, discountPct: pct / 100, channelVisible: visible,
        serviceType, assetPipelineId: pipelineId,
      })
      onSaved()
    } catch (e) {
      setSaveError((e as Error).message)
      setSaving(false)
    }
  }

  const example = {
    hsProductId: 'x', sku: null, name: 'Example', description: null,
    listPrice: EXAMPLE_LIST_PRICE, baseDiscountPct: valid ? pct / 100 : 0,
    resellerVisible: true, category: line.name, recurring: false,
    termMonths: null, active: true,
  }

  return (
    <SlideOver
      title={line.name}
      subtitle={`${line.productCount} active product${line.productCount === 1 ? '' : 's'}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn--primary"
            onClick={save}
            disabled={!editable || !dirty || saving}
            title={!dirty ? 'No changes to save' : undefined}
          >
            {saving ? 'Saving…' : 'Save band'}
          </button>
        </>
      }
    >
      {saveError && (
        <p className="pill pill--stop" style={{ display: 'block', marginBottom: 14 }}>{saveError}</p>
      )}

      <label className="fields__row" style={{ padding: 0, marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={visible}
          disabled={!editable || saving}
          onChange={(e) => setVisible(e.target.checked)}
        />
        Partners can quote this Product Line
      </label>

      <div className="toolbar" style={{ marginBottom: 6 }}>
        <label htmlFor="band-discount" style={{ minWidth: 130 }}>Discount off list</label>
        <input
          id="band-discount"
          className="input"
          type="number"
          min="0"
          max="100"
          step="0.5"
          value={discount}
          disabled={!editable || saving}
          onChange={(e) => setDiscount(e.target.value)}
          style={{ width: 90 }}
        />
        <span className="muted">%</span>
      </div>
      {!valid && <p className="pill pill--stop" style={{ display: 'inline-block' }}>Enter 0–100.</p>}

      {/* Decision 178: the provisioning-pack routing — which asset Service Type
          and asset pipeline this line's records are created on. Unrouted =
          the pack flags and skips the line's rows until this is set. */}
      <h3 style={{ marginTop: 22, marginBottom: 8 }}>Provisioning routing</h3>
      <div className="toolbar" style={{ marginBottom: 6 }}>
        <label htmlFor="band-service-type" style={{ minWidth: 130 }}>Asset Service Type</label>
        <select id="band-service-type" className="input" value={serviceType}
          disabled={!editable || saving} onChange={(e) => setServiceType(e.target.value)}>
          <option value="">— not routed —</option>
          {serviceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="toolbar" style={{ marginBottom: 6 }}>
        <label htmlFor="band-pipeline" style={{ minWidth: 130 }}>Asset pipeline</label>
        <select id="band-pipeline" className="input" value={pipelineId}
          disabled={!editable || saving} onChange={(e) => setPipelineId(e.target.value)}>
          <option value="">— not routed —</option>
          {assetPipelines.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>
      <p className="muted small" style={{ marginBottom: 4 }}>
        Provisioning packs create this line's asset records on the chosen pipeline (at its first
        stage, To Order) with the chosen Service Type. Leave unrouted and the pack skips the line,
        saying so in the preview.
      </p>

      <h3 style={{ marginTop: 22, marginBottom: 8 }}>
        Effect on a {formatGBP(EXAMPLE_LIST_PRICE)} product
      </h3>
      <table>
        <thead>
          <tr><th>Tier</th><th className="num">Multiplier</th><th className="num">Discount</th><th className="num">Buy price</th></tr>
        </thead>
        <tbody>
          {tiers.map((t) => {
            const priced = priceProduct(example, { mode: 'reseller', tier: t })
            return (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td className="num">×{t.multiplier}</td>
                <td className="num muted">{formatPct(priced.effectiveDiscountPct)}</td>
                <td className="num"><strong>{formatGBP(priced.buyPrice)}</strong></td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <p className="muted small" style={{ marginTop: 14 }}>
        Changing a band reprices open <strong>drafts</strong> only. Quotes that have been sent are
        frozen at their creation prices and never move.
      </p>

      {line.overrideCount > 0 && (
        <p className="small" style={{ marginTop: 12 }}>
          <span className="pill pill--info">{line.overrideCount} override{line.overrideCount > 1 ? 's' : ''}</span>{' '}
          product{line.overrideCount > 1 ? 's' : ''} on this line set their own discount or visibility
          in HubSpot, so this band does not apply to them.
        </p>
      )}

      {line.unconfigured && (
        <p className="small" style={{ marginTop: 14 }}>
          <span className="pill pill--warn">not configured</span> This line is new from HubSpot.
          Saving creates its band — it stays hidden from partners until you tick the allow-list,
          and it becomes available as an Asset library tag straight away.
        </p>
      )}

      {line.name === '(uncategorised)' && (
        <p className="small" style={{ marginTop: 14 }}>
          These products have no Product Line set in HubSpot. Setting one there is the tidier fix —
          this band is the safety net so they aren't silently unquotable in the meantime.
        </p>
      )}
    </SlideOver>
  )
}
